import { h, toast, avatar, confirmDialog, rowAction, tableScroll } from '../components/dom.js';
import { buildSearch, emptyState } from '../components/search.js';
import { buildSortControl } from '../components/sort-control.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import { esc } from './dashboard.js';
import { contactToVCard, downloadVCard } from './vcard.js';
import { mergeContactsByOwner, type MergedContact } from './merge.js';
import { formatDateTime } from '../lib/contact-timestamps.js';
import { dateTimeEl } from '../lib/timezone.js';
import { filterContacts, type ContactFilter } from './contacts-filter.js';
import { router } from '../router/router.js';
import { openContactCardModal } from './contact-card-modal.js';
import { applySavedOrder, orderKeys } from './field-order.js';
import { flattenContactNotes, primaryNoteSubscription } from '../lib/contact-notes.js';
import { RevWatcher } from '../lib/contact-refresh.js';
import { diffContacts, type ContactLite } from '../lib/contacts-diff.js';
import { contactsSummaryText, mountContactsUpdateBanner } from '../components/contacts-update-banner.js';
import { typeDisplayName, qualifierSubline } from './fields-catalog.js';

// toContactLite maps a flat Contact (one row per subscription) to the DOM-free
// ContactLite the diff engine consumes. Person identity = owner_id; display name
// prefers owner_name, then display_name, then owner_handle.
function toContactLite(c: Contact): ContactLite {
  return {
    subscription_id: c.subscription_id,
    owner_id: c.owner_id,
    name: c.owner_name || c.display_name || c.owner_handle || '',
    content_changed_at: c.content_changed_at ?? null,
  };
}

// fieldLabelBlock renders a synced field's PRIMARY type name plus, when the
// owner set a qualifier/annotation (`label`, e.g. "work"), a muted sub-line
// beneath it. Only shown when the qualifier is non-empty.
function fieldLabelBlock(f: ContactField): HTMLElement {
  // Custom fields (no catalog) store their display NAME in `label`; catalog
  // fields store an optional QUALIFIER there. Branch so the two aren't conflated.
  const isCustom = (f.type || '').startsWith('custom_');
  // Native hover-hint (browser `title`) showing ONLY the raw field id/key
  // (lowercase, underscores) — the stored type id, never the friendly name.
  const idTitle = f.type || f.key || '';
  if (isCustom) {
    const name = f.label || typeDisplayName(f.type);
    // Custom fields keep their NAME in `label`; their optional qualifier lives in
    // `annotation`. Surface it as the same muted sub-line catalog fields get.
    const qual = qualifierSubline(f.annotation);
    const head = h('small', { class: 'muted', title: idTitle }, name);
    if (!qual) return head;
    return h('div', {}, head,
      h('div', { class: 'muted', style: 'font-size:.78em;opacity:.8' }, qual));
  }
  const qual = qualifierSubline(f.label);
  const head = h('small', { class: 'muted', title: idTitle }, f.type_name || typeDisplayName(f.type));
  if (!qual) return head;
  return h('div', {}, head,
    h('div', { class: 'muted', style: 'font-size:.78em;opacity:.8' }, qual));
}
import {
  classifyRefreshError, classifyRefreshState,
  shouldAutoSync, markAutoSynced,
  cardSyncLabel, emptyCardTally, syncAllToast, quietSyncToast,
  cardRefreshToast, type CardSyncTally, type CardRefreshCounts,
} from '../lib/contact-sync.js';
import type { Contact, ContactField, Note, Subscription, Profile } from '../api/types.js';
import { profilesTheySeeField } from './subscriber-editor.js';

// localStorage key for a subscriber's LOCAL field-order override for one person
// (merged card, grouped by owner). Stored as an ordered array of field keys. This
// is a purely client-side preference and is NEVER sent to the server.
const fieldOrderKey = (ownerId: string): string => `vid.fieldorder.${ownerId}`;
function loadFieldOrder(ownerId: string): string[] | null {
  try {
    const raw = localStorage.getItem(fieldOrderKey(ownerId));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : null;
  } catch { return null; }
}
function saveFieldOrder(ownerId: string, keys: string[]): void {
  try { localStorage.setItem(fieldOrderKey(ownerId), JSON.stringify(keys)); } catch { /* ignore */ }
}
function clearFieldOrder(ownerId: string): void {
  try { localStorage.removeItem(fieldOrderKey(ownerId)); } catch { /* ignore */ }
}

// My Contacts — MVP-SCOPE §2: search is FULLY CLIENT-SIDE. We fetch the contact set
// once, decrypt private notes locally, build an in-memory index, and filter on every
// keystroke WITHOUT any server round-trip. Notes/labels are private to the user.
//
// A person may grant me SEVERAL of their profiles (one subscription each). We render
// ONE MERGED card per person (grouped by owner_id): the union of their shared fields
// across all my subscriptions to them. Refresh / Sync-all operate on every underlying
// subscription. See docs/CONTACT-SYNC.md.
export async function renderContacts(_p: Record<string, string> = {}, query?: URLSearchParams): Promise<void> {
  const main = renderShell('/contacts');

  const exportAll = h('a', { class: 'btn btn-ghost btn-sm', href: '#' }, 'Export all vCards');
  const cleanupBtn = h('a', { class: 'btn btn-ghost btn-sm', href: '#' }, 'Remove inactive');
  const syncAllBtn = h('a', { class: 'btn btn-ghost btn-sm', href: '#' }, 'Sync all');
  const syncBtn = h('a', { class: 'btn btn-primary btn-sm', href: '#' }, 'Sync to my phone');
  main.append(pageHead('My Contacts', 'Profiles you subscribe to — always up to date.',
    h('div', { class: 'row' }, syncAllBtn, cleanupBtn, syncBtn, exportAll)));

  const grid = h('div', { class: 'grid grid-3' });
  // List-view container (table, mirrors the Subscribers tab). Only one of
  // grid/listWrap is in the DOM at a time depending on the active view mode.
  const tbody = h('tbody');
  const listWrap = h('div', { class: 'card', style: 'padding:0' },
    tableScroll(h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'Name'), h('th', {}, 'VirtualID'), h('th', {}, 'Company'),
        h('th', {}, 'Labels'), h('th', {}, 'Last Changed'), h('th', {}, ''))),
      tbody)));

  // --- One fetch, then client-side everything (shared search component) ---
  const { data: contacts } = await api.contacts();
  // Also load who subscribes to MY profiles (+ my profile list) so each contact
  // card can show the shared "Profiles they see" field when this person is also a
  // subscriber of mine. Best-effort: on failure the field simply doesn't appear.
  const [subsRes, profRes] = await Promise.all([
    api.subscribers().catch(() => ({ data: [] as Subscription[] })),
    api.profiles().catch(() => ({ data: [] as Profile[] })),
  ]);
  let mySubscribers: Subscription[] = subsRes.data;
  const myProfiles: Profile[] = profRes.data;
  // Map a person's handle -> their ACTIVE subscriptions to my profiles.
  const subsByHandle = (): Map<string, Subscription[]> => {
    const map = new Map<string, Subscription[]>();
    for (const s of mySubscribers) {
      if (s.status !== 'active') continue;
      const key = (s.subscriber?.username || '').toLowerCase();
      if (!key) continue;
      (map.get(key) || map.set(key, []).get(key)!).push(s);
    }
    return map;
  };
  const reloadMySubscribers = async (): Promise<void> => {
    try { mySubscribers = (await api.subscribers()).data; } catch { /* keep old */ }
    search.setItems(merged());
  };
  // --- View mode: 'cards' (full mergedCard grid) vs 'list' (compact table). ---
  const extrasFor = (m: MergedContact): MergedCardExtras => ({
    subscriberSubs: m.handle ? (subsByHandle().get(m.handle.toLowerCase()) || []) : [],
    profiles: myProfiles,
    reloadSubs: reloadMySubscribers,
  });

  // Precedence: ?view= query > saved localStorage pref > auto (list when >10
  // contacts, else cards). Persisted so the choice sticks across visits.
  // EXCEPTION: a single-contact deep-link (/contacts/:handle, which arrives with
  // ?focus=<handle>) always opens in Card view so the user sees that person's
  // full "opened" card, not a one-row list. This overrides the saved/auto pref
  // (but an explicit ?view= still wins, so a deliberate choice is respected).
  const VIEW_KEY = 'vid_contacts_view';
  const peopleCount = mergeContactsByOwner(contacts).length;
  const savedView = (() => { try { return localStorage.getItem(VIEW_KEY); } catch { return null; } })();
  const queryView = query?.get('view');
  const focusHandle = (query?.get('focus') || '').trim();
  let viewMode: 'cards' | 'list' =
    queryView === 'list' || queryView === 'cards' ? queryView
    : focusHandle ? 'cards'
    : savedView === 'list' || savedView === 'cards' ? savedView
    : peopleCount > 10 ? 'list' : 'cards';

  const rowFor = (m: MergedContact): HTMLElement => {
    const { title } = cardTitle(m);
    const handle = (m.handle || '').trim();
    const open = (e: Event) => {
      e.preventDefault();
      // Preserve the view the user is currently in (List/Cards) on the history
      // entry we're leaving, so browser Back restores THAT view — even though the
      // opened contact deep-link forces Card view. We rewrite the current list
      // entry's hash to carry ?view=<current> (replaceState, no new entry), then
      // navigate to the contact. Back pops to the list entry with the pinned view.
      if (handle) {
        try {
          const listHash = '/contacts?view=' + viewMode;
          history.replaceState(null, '', '#' + listHash);
        } catch { /* ignore */ }
        router.navigate('/contacts/' + encodeURIComponent(handle));
      } else openContactCardModal({ contact: m, username: m.displayName || '', showNavToContact: false, onRemoved: () => { removePerson(m); } });
    };
    const nameLink = h('a', { href: handle ? '#/contacts/' + encodeURIComponent(handle) : '#', style: 'text-decoration:none;font-weight:600' },
      avatar(title, 28), h('span', { style: 'margin-left:8px;vertical-align:middle' }, title));
    nameLink.addEventListener('click', open);
    // Preview: open the card in a modal without leaving the list.
    const preview = h('a', { href: '#', class: 'btn btn-ghost btn-sm' }, 'Preview');
    preview.addEventListener('click', (e) => {
      e.preventDefault();
      openContactCardModal({ contact: m, username: handle || m.displayName || '', showNavToContact: !!handle, onRemoved: () => { removePerson(m); }, ...extrasFor(m) });
    });
    const labels = m.labels.length
      ? h('div', { class: 'row', style: 'gap:4px;flex-wrap:wrap' }, ...m.labels.map((l) => h('span', { class: 'badge' }, l)))
      : h('span', { class: 'muted' }, '\u2014');
    return h('tr', {},
      h('td', {}, h('div', { class: 'row', style: 'align-items:center' }, nameLink)),
      h('td', {}, handle ? h('a', { class: 'muted', href: '#/id/' + handle, style: 'text-decoration:none' }, '@' + handle) : h('span', { class: 'muted' }, '\u2014')),
      h('td', {}, m.company || h('span', { class: 'muted' }, '\u2014')),
      h('td', {}, labels),
      h('td', {}, formatDateTime(m.lastChangedAt) ? dateTimeEl(m.lastChangedAt!) : h('span', { class: 'muted' }, '\u2014')),
      h('td', {}, h('div', { class: 'row', style: 'gap:6px;justify-content:flex-end' }, preview,
        h('a', { href: handle ? '#/contacts/' + encodeURIComponent(handle) : '#', class: 'btn btn-primary btn-sm', onclick: open }, 'Open'))));
  };

  // Remove every subscription row for a person from the local set + re-render.
  const removePerson = (m: MergedContact): void => {
    for (const sub of m.subscriptions) {
      const ci = contacts.findIndex((x) => x.subscription_id === sub.subscription_id);
      if (ci >= 0) contacts.splice(ci, 1);
    }
    search.setItems(merged());
  };

  const render: (matches: MergedContact[]) => void = (matches) => {
    if (viewMode === 'list') {
      tbody.replaceChildren(...matches.map(rowFor));
      if (!matches.length) tbody.append(h('tr', {}, h('td', { colspan: '6', class: 'muted', style: 'text-align:center;padding:24px' }, 'No contacts.')));
    } else {
      grid.replaceChildren(...matches.map((m) => mergedCard(m, () => removePerson(m),
        () => search.setItems(merged()), extrasFor(m))));
      if (!matches.length) grid.append(emptyState('contacts'));
    }
  };


  // Filter by "profile they see" — same concept as the Subscribers page's profile
  // pills, but from the contact side: keep only contacts who are ALSO a subscriber
  // of mine AND who see the selected one of MY profiles. URL-driven (`?profile=<id>`)
  // so it behaves and deep-links exactly like the Subscribers filter. Empty = All.
  const filterProfile = query?.get('profile') || '';
  const seesProfile = (m: MergedContact, profileId: string): boolean =>
    contactSeesProfile(m.handle, subsByHandle(), profileId);
  const filterByProfileSeen = (list: MergedContact[]): MergedContact[] =>
    filterProfile ? list.filter((m) => seesProfile(m, filterProfile)) : list;
  // Active list filter (All vs Pending Approval). Applied on top of the merge.
  let activeFilter: ContactFilter = 'all';
  // Sort control (shared compact control): by name or by when I subscribed, with
  // asc/desc toggle. Applied to the merged list before the search index (which
  // preserves order).
  const sort = buildSortControl(
    [{ value: 'name', label: 'Name', defaultDir: 'asc' },
     { value: 'subscribed', label: 'Subscribed', defaultDir: 'desc' }],
    () => search.setItems(merged()));
  const subscribedAt = (m: MergedContact): string => {
    // Earliest 'since' across this person's subscriptions = when I first subscribed.
    let earliest = '';
    for (const s of m.subscriptions) {
      const v = s.added_at || '';
      if (v && (!earliest || v < earliest)) earliest = v;
    }
    return earliest;
  };
  const sortContacts = (list: MergedContact[]): MergedContact[] => {
    const out = [...list];
    const key = sort.key();
    if (key === 'name') {
      out.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' }));
    } else {
      out.sort((a, b) => subscribedAt(a).localeCompare(subscribedAt(b)));
    }
    if (sort.dir() === 'desc') out.reverse();
    return out;
  };
  const merged = () => sortContacts(filterContacts(filterByProfileSeen(mergeContactsByOwner(contacts)), activeFilter));

  const search = buildSearch<MergedContact>({
    noun: 'contacts',
    placeholder: 'Search by name, company, label, or note text (or id:<username>)…',
    hint: 'Search matches <strong>name, company, labels, and note text</strong> — entirely on this device (no per-keystroke server calls). Your notes &amp; labels are private and stored encrypted; the other person never sees them.',
    blob: (m) => [
      m.displayName, m.company,
      ...m.labels,
      ...m.fields.map((f) => f.value),
      ...m.subscriptions.map((c) => c.note),
    ].filter(Boolean).join(' '),
    pillsFor: (items) => [...new Set(items.flatMap((m) => m.labels))].map((l) => ({ label: l, value: l })),
    itemMatchesPill: (m, value) => m.labels.includes(value),
    // Scoped query support: `id:<username>` matches only the VirtualID handle.
    fields: { id: (m) => m.handle || m.displayName || '' },
    render,
  });
  main.append(search.el, grid, listWrap, portalFooter());

  // View-mode toggle (Cards / List) — mirrors the segmented filter look. Swaps
  // which container is visible, persists the choice, and re-renders. Placed with
  // the filter bar below.
  const applyView = (v: 'cards' | 'list', persist = true): void => {
    viewMode = v;
    if (persist) { try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ } }
    grid.style.display = v === 'cards' ? '' : 'none';
    listWrap.style.display = v === 'list' ? '' : 'none';
    segCards.className = 'btn btn-sm' + (v === 'cards' ? ' btn-primary' : '');
    segList.className = 'btn btn-sm' + (v === 'list' ? ' btn-primary' : '');
    search.setItems(merged());
  };
  const segCards = h('button', { class: 'btn btn-sm', type: 'button', title: 'Show full cards' }, 'Cards') as HTMLButtonElement;
  const segList = h('button', { class: 'btn btn-sm', type: 'button', title: 'Show compact list' }, 'List') as HTMLButtonElement;
  segCards.addEventListener('click', () => applyView('cards'));
  segList.addEventListener('click', () => applyView('list'));
  const viewToggle = h('div', { class: 'row', style: 'gap:8px' }, segCards, segList);

  // Segmented filter: All vs Pending Approval. Placed just above the results so
  // the requester can isolate tentative (pending-approval) contacts.
  const segAll = h('button', { class: 'btn btn-sm btn-primary', type: 'button' }, 'All') as HTMLButtonElement;
  const segPending = h('button', { class: 'btn btn-sm', type: 'button' }, 'Pending Approval') as HTMLButtonElement;
  const applyFilter = (f: ContactFilter): void => {
    activeFilter = f;
    segAll.className = 'btn btn-sm' + (f === 'all' ? ' btn-primary' : '');
    segPending.className = 'btn btn-sm' + (f === 'pending' ? ' btn-primary' : '');
    search.setItems(merged());
  };
  segAll.addEventListener('click', () => applyFilter('all'));
  segPending.addEventListener('click', () => applyFilter('pending'));
  // Sort dropdown — order the list by name or by subscribed time.
  const filterBar = h('div', { class: 'row between', style: 'gap:8px;margin-bottom:10px;align-items:center' },
    h('div', { class: 'row', style: 'gap:8px' }, segAll, segPending),
    h('div', { class: 'row', style: 'gap:8px;align-items:center' }, viewToggle, sort.el));
  search.el.before(filterBar);

  // "Filter by profile they see" pills — same look/feel as the Subscribers page.
  // Only shown when I actually have profiles; URL-driven so it deep-links and
  // toggles identically. Sits above the segmented All/Pending bar.
  if (myProfiles.length) {
    const pills = h('div', { class: 'pill-row', style: 'margin-bottom:12px' },
      h('span', { class: 'muted', style: 'align-self:center' }, h('small', {}, 'Filter by profile they see:')),
      contactPill('All', '/contacts', !filterProfile));
    for (const p of myProfiles) {
      pills.append(contactPill(p.name, '/contacts?profile=' + encodeURIComponent(p.id), filterProfile === p.id));
    }
    filterBar.before(pills);
  }

  applyView(viewMode, false);
  search.setItems(merged());
  // "Show on My Contacts" link, which passes `q=id:<username>`) pre-fills the
  // search so only that contact is shown on arrival.
  const initialQuery = query?.get('q');
  if (initialQuery) { search.input.value = initialQuery; search.refresh(); }
  exportAll.addEventListener('click', (e) => {
    e.preventDefault();
    const vcf = merged().map((m) => contactToVCard(mergedToContact(m))).join('\n');
    downloadVCard('virtualid-contacts.vcf', vcf);
  });

  // Realtime-ish updates WITHOUT a full page reload. Native EventSource can't
  // carry our auth headers (same constraint as approvals inbox + join banner),
  // so we SHORT-POLL. Two jobs on the same interval:
  //  1) Un-gray an approved contact (pending -> active) — poll /contacts while
  //     anything is still "Pending Approval".
  //  2) Live field-update fanout — poll /contacts/refresh-signal EVERY tick. When
  //     an owner edits/creates/deletes a field we can see, the backend bumps our
  //     per-subscriber rev; on a rev CHANGE we schedule a JITTERED /contacts
  //     refetch spread over a window proportional to active_peers, so N
  //     subscribers don't stampede the backend at once. See docs/CONTACT-SYNC.md
  //     + portal/src/lib/contact-refresh.ts. The periodic /contacts poll remains
  //     the correctness fallback across horizontally-scaled instances.
  const POLL_MS = 3000;
  const anyPending = (): boolean => contacts.some((c) => c.pending_approval === true);
  const pendingSig = (): string =>
    contacts.filter((c) => c.pending_approval === true).map((c) => c.subscription_id).sort().join(',');
  let poll: ReturnType<typeof setInterval> | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  const watcher = new RevWatcher();
  let primedSignal = false;
  const alive = (): boolean => document.body.contains(grid);

  const clearTimers = (): void => {
    if (poll) { clearInterval(poll); poll = null; }
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
  };

  // showContactsSummary computes a person-level diff between two snapshots and,
  // when something is new/updated, surfaces the auto-dismissing top banner.
  const showContactsSummary = (prev: Contact[], next: Contact[]): void => {
    if (!alive()) return;
    const diff = diffContacts(prev.map(toContactLite), next.map(toContactLite));
    if (diff.addedCount + diff.updatedCount === 0) return;
    mountContactsUpdateBanner(main, contactsSummaryText(diff));
  };

  // Re-pull /contacts and re-merge in place (shared by pending-poll + jittered
  // live-refresh). Preserves the array reference used by closures. Diffs the
  // pre/post snapshots and shows a "N new / N updated" summary banner on change.
  const refetchContacts = async (): Promise<void> => {
    const prev = contacts.slice();
    const { data: fresh } = await api.contacts();
    if (!alive()) return;
    contacts.splice(0, contacts.length, ...fresh);
    search.setItems(merged());
    showContactsSummary(prev, fresh);
  };

  const tick = async (): Promise<void> => {
    if (!alive()) { clearTimers(); return; }
    // (2) Live field-update signal — cheap, runs every tick even when idle.
    try {
      const sig = await api.contactsRefreshSignal();
      if (!alive()) return;
      if (!primedSignal) {
        watcher.prime(sig.rev); // baseline: don't refresh on first observation
        primedSignal = true;
      } else {
        const decision = watcher.observe(sig);
        if (decision.action === 'schedule' && !refreshTimer) {
          refreshTimer = setTimeout(() => {
            refreshTimer = null;
            void refetchContacts().catch(() => { /* transient */ }).finally(() => watcher.settle());
          }, decision.delayMs);
        }
      }
    } catch { /* transient; keep current */ }
    // (1) Pending-approval un-gray — only while something is pending.
    if (!anyPending()) return;
    const before = pendingSig();
    try {
      const { data: fresh } = await api.contacts();
      if (!alive()) return;
      contacts.splice(0, contacts.length, ...fresh);
      if (pendingSig() !== before) search.setItems(merged());
    } catch { /* transient; keep current */ }
  };
  poll = setInterval(() => void tick(), POLL_MS);

  // Visibility / reconnect quick-check: when the user returns to the app after
  // inactivity (tab re-shown or window refocused), immediately run one poll
  // cycle so the contacts view catches up without waiting for the next interval.
  // Debounced so rapid focus/visibility bursts don't spam; listeners are removed
  // on teardown (clearTimers) to avoid leaking across route changes.
  let visDebounce: ReturnType<typeof setTimeout> | null = null;
  const onVisible = (): void => {
    if (document.visibilityState === 'hidden') return;
    if (!alive()) { clearTimers(); return; }
    if (visDebounce) return; // already scheduled
    visDebounce = setTimeout(() => {
      visDebounce = null;
      void tick();
    }, 250);
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);

  syncBtn.addEventListener('click', (e) => { e.preventDefault(); toast('Phone sync is manual-groundwork in MVP (CardDAV auto-sync is post-MVP).', 'warn'); });

  // Remove inactive: bulk-remove every contact whose subscriptions are ALL revoked
  // ("No longer shared"). Purges the dead rows server-side and updates instantly.
  cleanupBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    if ((cleanupBtn as HTMLAnchorElement).getAttribute('aria-busy') === 'true') return;
    const dead = merged().filter((m) => m.allRevoked);
    if (!dead.length) { toast('No inactive contacts to remove', 'ok'); return; }
    const n = dead.length;
    if (!confirmDialog(`Remove ${n} inactive contact${n > 1 ? 's' : ''}? These people stopped sharing with you; their frozen copies will be deleted.`)) return;
    cleanupBtn.setAttribute('aria-busy', 'true');
    const orig = cleanupBtn.textContent;
    let removed = 0, failed = 0;
    for (const m of dead) {
      for (const sub of m.subscriptions) {
        cleanupBtn.textContent = `Removing ${removed + 1}\u2026`;
        try {
          await api.unsubscribe(sub.subscription_id);
          const ci = contacts.findIndex((x) => x.subscription_id === sub.subscription_id);
          if (ci >= 0) contacts.splice(ci, 1);
          removed++;
        } catch { failed++; }
      }
    }
    cleanupBtn.textContent = orig;
    cleanupBtn.removeAttribute('aria-busy');
    search.setItems(merged());
    toast(failed ? `Removed ${removed} \u00b7 ${failed} failed` : `Removed ${removed} inactive`, failed ? 'warn' : 'ok');
  });

  // Sync all: iterate over the MERGED CARDS (one unit per person), refreshing
  // each card's subscriptions sequentially under the hood, but COUNTING once per
  // CARD. Progress + toast are phrased per card; benign skipped/revoked PROFILE
  // counts are never surfaced. Only a REAL per-card failure warns. When `quiet`
  // (auto path) no success toast is shown; only a real per-card failure warns.
  const syncAll = async (quiet: boolean): Promise<void> => {
    if ((syncAllBtn as HTMLAnchorElement).getAttribute('aria-busy') === 'true') return;
    syncAllBtn.setAttribute('aria-busy', 'true');
    const orig = syncAllBtn.textContent;
    // BUG-FIX: re-pull /contacts FIRST so "Sync all" DISCOVERS newly-added
    // contacts (previously it only iterated the already-loaded list). Diff the
    // pre-sync snapshot against the fresh pull and surface the summary banner.
    const preSync = contacts.slice();
    try {
      const { data: fresh } = await api.contacts();
      if (alive()) {
        contacts.splice(0, contacts.length, ...fresh);
        search.setItems(merged());
        showContactsSummary(preSync, fresh);
      }
    } catch { /* transient; sync the cards we already have */ }
    const cards = mergeContactsByOwner(contacts);
    const tally: CardSyncTally = emptyCardTally();
    for (let i = 0; i < cards.length; i++) {
      syncAllBtn.textContent = cardSyncLabel(i + 1, cards.length);
      let cardFailed = false;
      for (const c of cards[i].subscriptions) {
        try {
          const fresh = await api.refreshContact(c.subscription_id);
          c.fields = fresh.fields || [];
          c.sync_state = fresh.sync_state;
          c.last_synced_at = fresh.last_synced_at;
          c.content_changed_at = fresh.content_changed_at;
          // Reflect approval transition: an approved (formerly pending) sub comes
          // back pending_approval=false + active, so the card un-grays from
          // "Pending Approval" to live WITHOUT a full page reload.
          c.pending_approval = fresh.pending_approval === true;
          c.status = fresh.status ?? c.status;
          c.live = fresh.live;
          // A revoked/disabled profile returns 200 (frozen fields) and is BENIGN
          // — it grays via this success path and never fails the card. On next
          // /contacts reload it is forgotten server-side (see docs/CONTACT-SYNC).
          const outcome = classifyRefreshState(fresh.sync_state);
          if (outcome === 'revoked') c.status = 'revoked';
        } catch (err) {
          const outcome = classifyRefreshError((err as Error).message);
          if (outcome === 'revoked') { c.sync_state = 'revoked'; c.status = 'revoked'; }
          else if (outcome === 'skipped') { c.sync_state = 'profile_disabled'; }
          else { cardFailed = true; } // a REAL failure marks the CARD failed
        }
      }
      if (cardFailed) tally.failed++; else tally.synced++;
    }
    syncAllBtn.textContent = orig;
    syncAllBtn.removeAttribute('aria-busy');
    search.setItems(merged());
    if (quiet) {
      const t = quietSyncToast(tally);
      if (t) toast(t.text, t.severity);
      return;
    }
    const done = syncAllToast(tally);
    toast(done.text, done.severity);
  };
  syncAllBtn.addEventListener('click', (e) => { e.preventDefault(); void syncAll(false); });

  // Auto "Sync all" ONCE per login session on landing on My Contacts. Renders
  // immediately (above), then fires the shared syncAll() in the BACKGROUND, quiet
  // (no success toast). Gated by a sessionStorage flag so later visits the SAME
  // session don't re-auto-sync (manual still works); a fresh LOGIN/logout resets
  // the flag (see session.set()/clear()). Guarded against a double renderContacts.
  try {
    if (shouldAutoSync(sessionStorage)) {
      markAutoSynced(sessionStorage);
      void syncAll(true);
    }
  } catch { /* no sessionStorage — skip auto-sync */ }
}

// contactSeesProfile: true when the contact (by handle) has an ACTIVE-mapped
// subscription to the given one of MY profiles. Pure + exported for unit tests;
// `subsByHandle` is the lower-cased handle -> subscriptions index built by the
// view (already filtered to active). Handle match is case-insensitive.
export function contactSeesProfile(
  handle: string | undefined,
  subsByHandle: Map<string, Subscription[]>,
  profileId: string,
): boolean {
  if (!handle) return false;
  const subs = subsByHandle.get(handle.toLowerCase()) || [];
  return subs.some((s) => s.profile_id === profileId);
}

// contactPill renders a URL-driven filter pill, identical in look/feel to the
// Subscribers page's profile pills (badge, brand-highlighted when active).
function contactPill(label: string, href: string, active: boolean): HTMLElement {
  return h('a', { class: 'badge ' + (active ? 'badge-brand' : ''), href: '#' + href, style: 'text-decoration:none' }, label);
}

// mergedToContact projects a merged card back to a single Contact shape for vCard
// export (union of fields, first note as the note).
function mergedToContact(m: MergedContact): Contact {
  const first = m.subscriptions[0];
  return {
    ...first,
    display_name: m.displayName,
    company: m.company,
    fields: m.fields,
    labels: m.labels,
    note: m.subscriptions.map((c) => c.note).find(Boolean),
  };
}

// cardTitle derives the friendly contact header: prefer the person's given+family
// name (from synced fields); fall back to the username/display_name only if BOTH
// are empty. Under the title we show a `virtualid.one/id/<handle>` link (when the
// owner handle is known) and the company (when present). Avatar initials prefer
// the given+family name, else the display name.
function cardTitle(m: MergedContact): { title: string; sub: HTMLElement[]; initialsSeed: string } {
  const given = (m.givenName || '').trim();
  const family = (m.familyName || '').trim();
  const nameTitle = [given, family].filter(Boolean).join(' ');
  const title = nameTitle || m.displayName || '\u2014';
  const initialsSeed = nameTitle || m.displayName || '?';

  const sub: HTMLElement[] = [];
  const handle = (m.handle || '').trim();
  if (handle) {
    sub.push(h('a', {
      class: 'muted handle-link',
      style: 'display:block;font-size:.85em',
      href: '#/id/' + handle,
    }, 'virtualid.one/id/' + handle));
  }
  if (m.company) sub.push(h('small', { style: 'display:block' }, m.company));
  // Two muted timestamp lines: Created (first added) and Last Changed (last real
  // content/access change). Rendered small/muted, consistent with card meta.
  // display:block so link + Created + Last Changed each sit on their own line.
  const created = m.createdAt;
  const changed = m.lastChangedAt;
  if (formatDateTime(created)) sub.push(h('small', { class: 'muted', style: 'display:block;font-size:.8em' }, 'Created: ', dateTimeEl(created!)));
  if (formatDateTime(changed)) sub.push(h('small', { class: 'muted', style: 'display:block;font-size:.8em' }, 'Last Changed: ', dateTimeEl(changed!)));
  return { title, sub, initialsSeed };
}

// mergedCard renders a person's contact card (used on My Contacts, and reused in
// the Subscribers page popup for a consistent card design). `onRemoveAll` is called
// after every subscription for the person is removed; `reRender` re-renders in place.
export interface MergedCardExtras {
  // When this person is also a SUBSCRIBER to my profiles: their active
  // subscriptions + my profile list, so the card can render the shared, clickable
  // "Profiles they see" field (opens the subscriber editor). Rendered INSIDE the
  // card so it appears identically on My Contacts and in every popup.
  subscriberSubs?: Subscription[];
  profiles?: Profile[];
  reloadSubs?: () => Promise<void>;
  // Optional "Show on My Contacts" action (label + handler). Rendered as a normal
  // in-card button so it's readable on the card surface (not on the modal overlay).
  navToContact?: { label: string; onClick: () => void };
}

export function mergedCard(m: MergedContact, onRemoveAll: () => void, reRender: () => void, extras?: MergedCardExtras): HTMLElement {
  const liveBadge = m.pendingApproval
    ? h('span', { class: 'badge badge-warn' }, 'Pending Approval')
    : m.allRevoked
    ? h('span', { class: 'badge badge-warn' }, 'No longer shared')
    : m.allDisabled
      ? h('span', { class: 'badge badge-warn' }, 'Temporarily disabled')
      : m.live
        ? h('span', { class: 'badge badge-ok' }, 'Live')
        : h('span', { class: 'badge badge-warn' }, 'Not syncing');

  // Privacy-preserving profile summary. We NEVER render profile names here.
  // Always show a primary "Public" pill ("Public (disabled)" when the person's
  // public profile subscription is disabled), then a "+N" pill counting the
  // ADDITIONAL non-revoked, non-public profiles they granted (revoked excluded).
  const publicPill = h('span', { class: 'badge ' + (m.publicDisabled ? 'badge-warn' : '') },
    m.publicDisabled ? 'Public (disabled)' : 'Public');
  const extraPill = m.extraProfileCount > 0
    ? h('span', { class: 'badge' }, '+' + m.extraProfileCount)
    : null;
  const profileBadges = extraPill ? [publicPill, extraPill] : [publicPill];

  const pills = h('div', { class: 'pill-row', style: 'margin-top:12px' }, liveBadge,
    ...profileBadges,
    ...m.labels.map((l) => h('span', { class: 'badge' }, l)));

  const disabledBanner = m.pendingApproval
    ? h('div', { style: 'margin-top:12px;padding:10px 12px;background:var(--warn-soft,#fff7ed);border:1px solid var(--border);border-radius:10px' },
        h('small', { class: 'muted' }, 'This person subscribed to you and shared a profile back. Add them to keep it, or Delete to decline. For now you only see their public details.'))
    : m.allRevoked
    ? h('div', { style: 'margin-top:12px;padding:10px 12px;background:var(--warn-soft,#fff7ed);border:1px solid var(--border);border-radius:10px' },
        h('small', { class: 'muted' }, 'This person stopped sharing with you. Your saved copy is frozen and no longer updates. You can remove them from your contacts.'))
    : m.allDisabled
    ? h('div', { style: 'margin-top:12px;padding:10px 12px;background:var(--warn-soft,#fff7ed);border:1px solid var(--border);border-radius:10px' },
        h('small', { class: 'muted' }, 'Every profile this person shares with you is temporarily disabled. Synced details are paused and will resume when re-enabled.'))
    : null;

  const notesEl = notesSection(m);
  const fieldsEl = fieldsSection(m, reRender);

  const vcardBtn = h('a', { class: 'btn btn-ghost btn-sm', href: '#' }, 'vCard');
  vcardBtn.addEventListener('click', (e) => { e.preventDefault(); downloadVCard(`${m.displayName}.vcf`, contactToVCard(mergedToContact(m))); });

  // Refresh: re-pull EVERY subscription for this person, sequentially, then re-merge.
  const pullBtn = h('a', { class: 'btn btn-ghost btn-sm', href: '#' }, 'Refresh');
  pullBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    const orig = pullBtn.textContent;
    pullBtn.textContent = 'Refreshing…';
    const counts: CardRefreshCounts = { synced: 0, benign: 0, failed: 0, total: m.subscriptions.length };
    for (const c of m.subscriptions) {
      try {
        const fresh = await api.refreshContact(c.subscription_id);
        c.fields = fresh.fields || [];
        c.sync_state = fresh.sync_state;
        c.last_synced_at = fresh.last_synced_at;
        c.content_changed_at = fresh.content_changed_at;
        // Reflect approval transition (see Sync-all): un-gray pending -> active
        // on explicit Refresh without a full page reload.
        c.pending_approval = fresh.pending_approval === true;
        c.status = fresh.status ?? c.status;
        c.live = fresh.live;
        // A revoked/disabled profile returns 200 (frozen fields) — BENIGN. It is
        // forgotten server-side on the next /contacts reload when the person
        // still shares another active profile; here we just don't count it as a
        // failure. No per-profile churn messaging.
        const outcome = classifyRefreshState(fresh.sync_state);
        if (outcome === 'revoked') { c.status = 'revoked'; counts.benign++; }
        else if (outcome === 'skipped') { counts.benign++; }
        else { counts.synced++; }
      } catch (err) {
        const outcome = classifyRefreshError((err as Error).message);
        // Defensive: a genuine revoked/disabled error (stale client) still grays.
        if (outcome === 'revoked') { c.sync_state = 'revoked'; c.status = 'revoked'; counts.benign++; }
        else if (outcome === 'skipped') { c.sync_state = 'profile_disabled'; counts.benign++; }
        else { counts.failed++; }
      }
    }
    pullBtn.textContent = orig;
    reRender();
    // Whole-contact-gone: every profile of this person is now revoked/disabled
    // (nothing left to sync). Computed from the just-mutated subscriptions
    // (m.allRevoked was captured at merge time, before this refresh).
    const allGone = m.subscriptions.every(
      (c) => c.sync_state === 'revoked' || c.status === 'revoked' || c.sync_state === 'profile_disabled');
    // A PARTIAL revoke/disable where the person still shares ≥1 profile reads
    // exactly "Refreshed" — NO mention of the dropped profile (those rows are
    // forgotten on the next list reload). Only a real failure or a
    // whole-contact-gone is non-plain. See docs/CONTACT-SYNC.md.
    const out = cardRefreshToast(counts, allGone);
    toast(out.text, out.severity);
  });

  const removeBtn = rowAction('Remove', async () => {
    const n = m.subscriptions.length;
    const msg = n > 1
      ? `Remove “${m.displayName}”? You’ll stop syncing all ${n} of their profiles, and they’ll no longer see you as a subscriber.`
      : `Remove “${m.displayName}”? You’ll stop syncing their details, and they’ll no longer see you as a subscriber.`;
    if (!confirmDialog(msg)) return;
    removeBtn.textContent = 'Removing…';
    try {
      for (const c of m.subscriptions) await api.unsubscribe(c.subscription_id);
      toast('Contact removed', 'ok');
      onRemoveAll();
    } catch (err) {
      toast((err as Error).message, 'danger');
      removeBtn.textContent = 'Remove';
    }
  }, { variant: 'danger' });

  const title = cardTitle(m);
  const cardHandle = (m.handle || '').trim();
  // Card header title → authed deep-link to this single contact (contacts/<handle>).
  // Only linkified when the handle is known; otherwise plain text.
  const titleEl = cardHandle
    ? (() => {
        const a = h('a', { href: '#/contacts/' + encodeURIComponent(cardHandle), style: 'text-decoration:none;color:inherit' }, title.title);
        a.addEventListener('click', (e) => { e.preventDefault(); router.navigate('/contacts/' + encodeURIComponent(cardHandle)); });
        return h('h3', { style: 'margin:0' }, a);
      })()
    : h('h3', { style: 'margin:0' }, title.title);
  // Add button (top-right) for a pending reciprocal share-back: accept it, which
  // un-grays the card and removes the button. Delete uses the existing Remove.
  const pendingSub = m.subscriptions.find((c) => c.pending_approval === true);
  let addBtn: HTMLElement | null = null;
  if (m.pendingApproval && pendingSub) {
    const btn = h('button', { class: 'btn btn-primary btn-sm' }, 'Add');
    btn.addEventListener('click', async () => {
      btn.setAttribute('disabled', 'true');
      btn.textContent = 'Adding…';
      try {
        await api.acceptContact(pendingSub.subscription_id);
        // Un-gray every subscription of this contact that was pending.
        for (const c of m.subscriptions) {
          if (c.pending_approval) { c.pending_approval = false; c.status = 'active'; c.live = true; }
        }
        toast('Contact added', 'ok');
        reRender();
      } catch (err) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add';
        toast((err as Error).message || 'Could not add contact', 'danger');
      }
    });
    addBtn = btn;
  }
  const header = h('div', { class: 'row between' },
    h('div', { class: 'row' }, avatar(title.initialsSeed),
      h('div', {}, titleEl,
        ...title.sub)),
    ...(addBtn ? [addBtn] : []));
  // Shared "Profiles they see" field (when this person subscribes to my profiles)
  // + optional "Show on My Contacts" button, rendered INSIDE the card so they're
  // readable on the card surface and appear identically everywhere the card shows.
  const profilesField = extras
    ? profilesTheySeeField(extras.subscriberSubs || [], extras.profiles || [], extras.reloadSubs || (async () => {}))
    : null;
  const navBtn = extras?.navToContact
    ? (() => {
        const b = h('button', { class: 'btn btn-ghost btn-sm', style: 'margin-top:12px' }, extras.navToContact.label);
        b.addEventListener('click', extras.navToContact!.onClick);
        return b;
      })()
    : null;
  const card = h('div', { class: 'card' + (m.pendingApproval ? ' card-stale card-pending' : m.allRevoked ? ' card-stale card-revoked' : m.live ? '' : ' card-stale') },
    header,
    pills, disabledBanner, fieldsEl, notesEl,
    h('div', { class: 'row', style: 'margin-top:12px' }, pullBtn, vcardBtn, removeBtn),
    ...(profilesField ? [profilesField] : []),
    ...(navBtn ? [navBtn] : []));
  return card;
}

// Synced-fields section: renders the MERGED union of owner-profile fields. Non-syncing
// (stale in ALL subscriptions) fields render GRAYED OUT with a "not syncing" tag and a
// Remove control that drops the row from EVERY subscription that carries it.
type FieldsSection = HTMLElement & { rerender: () => void; _ordered: ContactField[] };
function fieldsSection(m: MergedContact, reRender: () => void): FieldsSection {
  const wrap = h('div', { style: 'margin-top:12px' }) as FieldsSection;
  let dragKey: string | null = null;

  const removeStale = async (f: ContactField): Promise<void> => {
    // Drop from every subscription that still carries this stale key.
    for (const c of m.subscriptions) {
      if ((c.fields || []).some((x) => x.key === f.key && !x.syncing)) {
        await api.removeContactField(c.subscription_id, f.key);
        c.fields = (c.fields || []).filter((x) => x.key !== f.key);
      }
    }
    reRender();
    toast('Field removed', 'ok');
  };

  const fieldRow = (f: ContactField, onReorder: () => void): HTMLElement => {
    const stale = !f.syncing;
    const valueStyle = stale
      ? 'margin:0;color:var(--muted,#9ca3af);text-decoration:none'
      : 'margin:0;color:var(--fg)';
    const right: HTMLElement[] = [];
    if (stale) {
      right.push(h('span', { class: 'badge badge-warn' }, 'not syncing'));
      const rm = h('a', { href: '#', title: 'Remove this non-syncing field', style: 'color:var(--danger)' }, h('small', {}, '\u2715'));
      rm.addEventListener('click', (e) => {
        e.preventDefault();
        removeStale(f).catch((err) => toast((err as Error).message, 'danger'));
      });
      right.push(rm);
    }
    const grip = h('span', { class: 'drag-handle', title: 'Drag to reorder', 'aria-hidden': 'true' }, '\u22ee\u22ee');
    const row = h('div', { class: 'row between', draggable: 'true', style: 'align-items:center;margin-top:6px' + (stale ? ';opacity:0.7' : '') },
      h('div', { class: 'row', style: 'gap:8px;align-items:center' }, grip,
        h('div', {},
          fieldLabelBlock(f),
          h('p', { style: valueStyle }, f.value || '\u2014'))),
      h('span', { class: 'row', style: 'gap:8px;align-items:center' }, ...right)) as HTMLElement;
    (row as HTMLElement).dataset.key = f.key;
    row.addEventListener('dragstart', (e) => {
      dragKey = f.key; row.classList.add('dragging');
      if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', f.key); }
    });
    row.addEventListener('dragend', () => { row.classList.remove('dragging'); wrap.querySelectorAll('.drag-over').forEach((el) => el.classList.remove('drag-over')); dragKey = null; });
    row.addEventListener('dragover', (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; row.classList.add('drag-over'); });
    row.addEventListener('dragleave', () => row.classList.remove('drag-over'));
    row.addEventListener('drop', (e) => {
      e.preventDefault();
      row.classList.remove('drag-over');
      if (!dragKey || dragKey === f.key) return;
      const list = wrap._ordered;
      const from = list.findIndex((x) => x.key === dragKey);
      const to = list.findIndex((x) => x.key === f.key);
      if (from < 0 || to < 0) return;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      // Persist the subscriber's LOCAL override (localStorage only).
      saveFieldOrder(m.ownerId, orderKeys(list));
      onReorder();
    });
    return row;
  };

  wrap.rerender = (): void => {
    const fields = m.fields || [];
    if (!fields.length) { wrap.replaceChildren(); wrap._ordered = []; return; }
    const saved = loadFieldOrder(m.ownerId);
    // Default = owner order (already applied in merge). Apply local override if any;
    // fields not in the override (e.g. newly-arrived) fall to the BOTTOM in owner order.
    const ordered = saved ? applySavedOrder(fields, saved) : [...fields];
    wrap._ordered = ordered;

    const header: HTMLElement[] = [h('small', { class: 'muted' }, 'Shared details')];
    if (saved) {
      const restore = h('a', { href: '#', title: 'Restore the owner\u2019s original field order' }, h('small', {}, 'Restore original order'));
      restore.addEventListener('click', (e) => {
        e.preventDefault();
        clearFieldOrder(m.ownerId);
        wrap.rerender();
        toast('Restored original order', 'ok');
      });
      header.push(restore);
    }
    wrap.replaceChildren(
      h('div', { class: 'row between', style: 'align-items:center' }, ...header),
      ...ordered.map((f) => fieldRow(f, () => wrap.rerender())));
  };

  wrap.rerender();
  return wrap;
}

// Private-notes section: notes are stored PER-SUBSCRIPTION (each subscription row
// has its own encrypted notes), but we present them as ONE unified "Private
// notes 🔒" section per person (merged card) with a SINGLE "+ Add note" control,
// regardless of how many of their profiles I'm subscribed to. All notes across
// every subscription are shown together (newest-first); each row stays bound to
// ITS OWN subscription_id + note id so edit/delete hit the right subscription.
// New notes attach to a deterministic PRIMARY subscription. See docs/CONTACT-SYNC.md.
function notesSection(m: MergedContact): HTMLElement {
  const wrap = h('div', { style: 'margin-top:12px' });

  // Primary subscription for NEW notes (deterministic; see contact-notes.ts).
  const primary = (): Contact => primaryNoteSubscription(m.subscriptions)!;

  // Flatten every note across all subscriptions, newest-first (pure helper).
  const allNotes = (): { c: Contact; n: Note }[] => flattenContactNotes(m.subscriptions);

  const render = (): void => {
    const rows = allNotes();
    const children: HTMLElement[] = [
      h('div', { class: 'row between', style: 'align-items:center' },
        h('small', { class: 'muted' }, 'Private notes 🔒'), addLink()),
    ];
    if (!rows.length) {
      children.push(h('p', { class: 'muted', style: 'margin:6px 0 0' },
        h('small', {}, 'No notes yet — only you can see notes you add here.')));
    } else {
      for (const { c, n } of rows) children.push(noteRow(c, n));
    }
    wrap.replaceChildren(...children);
  };

  const addLink = (): HTMLElement => {
    const a = h('a', { href: '#' }, h('small', {}, '+ Add note'));
    a.addEventListener('click', (e) => { e.preventDefault(); openEditor(primary(), null); });
    return a;
  };

  const noteRow = (c: Contact, n: Note): HTMLElement => {
    const box = h('div', { style: 'margin-top:8px;padding:10px 12px;background:var(--bg-soft);border:1px solid var(--border);border-radius:10px' });
    const editA = rowAction('Edit', () => { openEditor(c, n); });
    const delA = rowAction('Remove', async () => {
      if (!confirmDialog('Remove this private note? This cannot be undone.')) return;
      try {
        if (n.id) await api.deleteNote(c.subscription_id, n.id);
        c.notes = (c.notes || []).filter((x) => x !== n);
        syncFirst(c);
        toast('Note removed', 'ok');
        render();
      } catch (err) { toast((err as Error).message, 'danger'); }
    }, { variant: 'danger' });
    box.append(
      h('div', { class: 'row between', style: 'align-items:center' },
        h('small', { class: 'muted' }, n.updated_at ? 'Updated' : 'Note'),
        h('span', { class: 'row-actions' }, editA, delA)),
      h('p', { style: 'margin:6px 0 0;color:var(--fg);white-space:pre-wrap', html: esc(n.body || '') }));
    return box;
  };

  const openEditor = (c: Contact, existing: Note | null): void => {
    const ta = h('textarea', { class: 'input', rows: '3', style: 'width:100%;resize:vertical',
      placeholder: 'Private note (encrypted, only you can read)…' }) as HTMLTextAreaElement;
    ta.value = existing?.body || '';
    const save = h('button', { class: 'btn btn-primary btn-sm' }, existing ? 'Save' : 'Add');
    const cancel = h('button', { class: 'btn btn-ghost btn-sm' }, 'Cancel');
    const editor = h('div', { style: 'margin-top:8px' }, ta,
      h('div', { class: 'row', style: 'margin-top:6px;gap:8px' }, save, cancel));
    wrap.append(editor);
    ta.focus();
    cancel.addEventListener('click', (e) => { e.preventDefault(); render(); });
    save.addEventListener('click', async (e) => {
      e.preventDefault();
      const body = ta.value.trim();
      if (!body) { toast('Note is empty', 'warn'); return; }
      save.textContent = 'Saving…'; (save as HTMLButtonElement).disabled = true;
      try {
        if (existing && existing.id) {
          const updated = await api.updateNote(c.subscription_id, existing.id, body);
          existing.body = updated?.body ?? body;
          existing.updated_at = updated?.updated_at ?? new Date().toISOString();
        } else {
          const created = await api.addNote(c.subscription_id, body);
          c.notes = c.notes || [];
          c.notes.push({ id: created?.id, body: created?.body ?? body, created_at: created?.created_at, updated_at: created?.updated_at });
        }
        syncFirst(c);
        toast('Note saved (encrypted)', 'ok');
        render();
      } catch (err) {
        toast((err as Error).message, 'danger');
        save.textContent = existing ? 'Save' : 'Add'; (save as HTMLButtonElement).disabled = false;
      }
    });
  };

  const syncFirst = (c: Contact): void => { c.note = c.notes && c.notes[0]?.body ? c.notes[0].body : undefined; };

  render();
  return wrap;
}
